import { useMutation, useQuery } from "@tanstack/react-query";
import { instance } from "api/apiBase";
import { Layout } from "providers/Layout";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  Button,
  ButtonSize,
  ButtonVariant,
  Checkbox,
  DateInput,
  Input
} from "shared/components";
import { BreadCrumbs } from "shared/components/BreadCrumbs";
import { Loader } from "shared/components/Loader";
import { Subscriber } from "views/rent-reporting-id/types";

import { getErrorMessage } from "utils/error";
import { Redirect } from "shared/components/Redirect";
import { useAuthContext } from "providers/Auth";
import { toast } from "react-toastify";
import { Row } from "./components/Row";
import { Section } from "./components/Section";

export const ResubscribeSubscriptionDetails = () => {
  const { type } = useAuthContext();
  const { renterId } = useParams();

  const navigate = useNavigate();
  const { data: renter, isFetching } = useQuery({
    queryKey: ["SUBSCRIBER_DETAIL", renterId],
    queryFn: () =>
      instance
        .get(`/portal/v1/customers/${renterId}`)
        .then(({ data: renter }: { data: Subscriber }) => renter)
  });

  const { mutate: resubscribe } = useMutation({
    mutationKey: ["RESUBSCRIBE", renterId],
    mutationFn: () => {
      return instance.post(`/portal/v1/customers/${renterId}/resubscribe`, {});
    },
    onSuccess: () => {
      toast.success("Subscription was successfully resumed");
      navigate(`/rent-reporting?type=unsubscribed`);
    },
    onError: (err) => {
      toast.error(getErrorMessage(err));
    }
  });

  const breadCrumbs = (
    <BreadCrumbs
      crumbs={[
        {
          title: "Rent reporting",
          href: "/rent-reporting"
        },
        {
          title: renter?.name || "",
          href: `/rent-reporting/${renterId}`
        },
        {
          title: "Subscription details",
          href: `/rent-reporting/${renterId}/resubscribe`
        }
      ]}
    />
  );

  const headerLayout = (
    <>
      <div className="border-b border-alpha-40 p-6">{breadCrumbs}</div>
      <div className="flex items-center justify-between border-b border-alpha-100 p-6">
        <h1 className="text-xl font-bold text-content-1000">
          View subscription details
        </h1>
      </div>
    </>
  );

  if (type.isSDK) return <Redirect href="/" />;

  if (isFetching)
    return (
      <Layout
        headerClassName="py-0"
        header={headerLayout}
        footer={
          <div className="flex justify-end gap-2">
            <Link to={`/rent-reporting/${renterId}`}>
              <Button size={ButtonSize.BASE} variant={ButtonVariant.BASE}>
                Cancel
              </Button>
            </Link>
            <Button size={ButtonSize.BASE} variant={ButtonVariant.PRIMARY}>
              Resume
            </Button>
          </div>
        }
      >
        <div className="flex justify-center">
          <Loader />
        </div>
      </Layout>
    );

  return (
    <Layout
      headerClassName="py-0"
      header={headerLayout}
      footer={
        <div className="flex justify-end gap-2">
          <Link to={`/rent-reporting/${renterId}`}>
            <Button size={ButtonSize.BASE} variant={ButtonVariant.BASE}>
              Cancel
            </Button>
          </Link>
          <Button
            size={ButtonSize.BASE}
            variant={ButtonVariant.PRIMARY}
            disabled={type.isPMS}
            onClick={() => resubscribe()}
          >
            Resume
          </Button>
        </div>
      }
    >
      <Section title="Subscription Details">
        <div className="w-1/2 pr-2">
          <Input
            id="address"
            label="Address"
            hideOptional
            readOnly
            value={renter?.address}
          />
        </div>

        <Row
          left={
            <Input
              id="property"
              label="Property"
              readOnly
              hideOptional
              value={renter?.property?.name}
            />
          }
          right={
            <Input
              id="unit"
              label="Unit"
              readOnly
              hideOptional
              value={renter?.unit}
            />
          }
        />
        <Row
          left={
            <DateInput
              id="move_in_date"
              placeholder="MM/DD/YYYY"
              label="Move-in date"
              hideOptional
              readOnly
              value={renter?.move_in_date}
            />
          }
          right={
            <>
              <DateInput
                id="lease_end_date"
                placeholder="MM/DD/YYYY"
                label="Lease end date"
                value={renter?.lease_end_date}
                readOnly
                hideOptional
              />
              <Checkbox
                id="lease_month_to_month"
                label="It’s month to month / no end date"
                checked={renter?.lease_month_to_month}
                disabled
                onChange={() => {}}
              />
            </>
          }
        />

        <div className="money-input-2 w-1/2 pr-2 mt-4">
          <Input
            id="monthly_rent_amount"
            value={(renter?.current_month_payment?.cents || 0) / 100 || ""}
            hideOptional
            readOnly
            type="money"
            label="Rent amount"
          />
        </div>
      </Section>

      <Section className="mt-10" title="Personal Details">
        <Row
          left={
            <Input
              id="first_name"
              label="Legal first name"
              readOnly
              hideOptional
              value={renter?.first_name}
            />
          }
          right={
            <Input
              id="last_name"
              label="Legal last name"
              readOnly
              hideOptional
              value={renter?.last_name}
            />
          }
        />

        <Row
          left={
            <Input
              id="email"
              label="Email"
              readOnly
              hideOptional
              value={renter?.email}
            />
          }
          right={
            <Input
              id="phone"
              label="Phone"
              readOnly
              hideOptional
              value={renter?.phone}
              placeholder="(123) 000-000"
              type="tel"
            />
          }
        />

        <div className="mt-4 w-1/2 pr-2">
          <DateInput
            id="dob"
            readOnly
            label="Date of birth"
            hideOptional
            value={renter?.dob}
          />
        </div>
      </Section>
    </Layout>
  );
};
